import React, { FC, useContext, useEffect, useRef } from "react";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { getCriticalityClass, getCriticalityLabel } from "@app/projects/network-assessment/provider-hub/utility";
import ProviderHubCriticalityService from "@app/projects/network-assessment/provider-hub/providerHubCriticalityService";
import { AggregateServicingProvider, MarketAlternative } from "@app/projects/network-assessment/provider-hub/interfaces";
import { useProviderLink } from "@app/projects/network-assessment/provider-hub/hooks/useProviderLink";
import TooltipWrapper from "@app/shared/TooltipWrapper";

interface ProviderProfileTopMarketAlternativesProps {
  selectedAccessPointId: number;
}

const ProviderProfileTopMarketAlternatives: FC<ProviderProfileTopMarketAlternativesProps> = (props: ProviderProfileTopMarketAlternativesProps) => {
  const { selectedAccessPointId } = props;

  const providerHubCriticalityService = useRef(new ProviderHubCriticalityService()).current;
  const {
    marketAlternatives,
    marketAlternativesError,
    marketAlternativesLoading,
    selectedProvider,
    setMarketAlternatives,
    setMarketAlternativesError,
    setMarketAlternativesLoading,
  } = useContext(ProviderHubContext);
  const getProviderLink = useProviderLink();

  useEffect(() => {
    if (selectedProvider && selectedAccessPointId) {
      setMarketAlternativesLoading(true);

      getMarketAlternatives(selectedProvider).then((marketAlternatives) => {
        setMarketAlternativesLoading(false);
        setMarketAlternatives(marketAlternatives);
      }, () => {
        setMarketAlternativesLoading(false);
        setMarketAlternativesError(true);
      });
    }
  }, [selectedProvider, selectedAccessPointId]);

  const getMarketAlternatives = async (selectedProvider: AggregateServicingProvider): Promise<MarketAlternative[]> => {
    return await providerHubCriticalityService.getMarketAlternatives(
      selectedProvider.projectRunConfigurationId, selectedAccessPointId);
  };

  const renderLoadingContent = (): React.ReactNode => {
    return (
      <div className="empty-state text-center">
        <QesIcon iconName={QesIcons.LoadingV1} className="qes-icon-spin" description="Loading out-of-network alternatives" />
      </div>
    );
  };

  const renderErrorContent = (): React.ReactNode => {
    return (
      <div className="alert alert-danger" role="alert">Failed to load out-of-network alternatives</div>
    );
  };

  const renderDataContent = (): React.ReactNode => {
    return (
      <div className="w-100">
        <div className="provider-profile__overview-row demibold">
          <div className="provider-profile__overview-title">
            Provider Name
          </div>
          <div className="provider-profile__overview-title">
            Adequacy Criticality
          </div>
        </div>
        {marketAlternatives.map((alternative) => (
          <div key={alternative.providerKey} className="provider-profile__overview-row">
            <div className="provider-profile__market-alternative-link">
              <TooltipWrapper text="View in new tab" position="left">
                <a target="_blank" rel="noreferrer" href={getProviderLink(alternative.providerKey.toLowerCase())}
                   data-testid={`provider-profile__market-alternative-link--${alternative.providerKey}`}>
                  {alternative.providerName}
                  <QesIcon iconName={QesIcons.ShareV4} description="open link in new tab" />
                </a>
              </TooltipWrapper>
            </div>
            <div className={`provider-profile__overview-value ${getCriticalityClass(alternative.score)}`}>
              {getCriticalityLabel(alternative.score)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (!marketAlternativesLoading && !marketAlternativesError && marketAlternatives.length === 0) {
    return null;
  }

  const getContent = (): React.ReactNode => {
    if (marketAlternativesLoading) return renderLoadingContent();
    if (marketAlternativesError) return renderErrorContent();
    return renderDataContent();
  };

  return (
    <div className="provider-profile__card provider-profile__card--no-padding">
      <h4 className="provider-profile__card-title">
        Top Out of Network Alternatives
      </h4>
      <div className="provider-profile__card-content">
        {getContent()}
      </div>
    </div>
  );
};

export default ProviderProfileTopMarketAlternatives;
